import { useState, useMemo, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import toast from 'react-hot-toast';
import {
  Search, Star, ShieldCheck, X, Plus, Clock, MessageSquare,
  CheckCircle2, AlertTriangle, Send,
} from 'lucide-react';

type ListingType = 'ad_placement' | 'vp_partnership';
type TabKey = 'ads' | 'vp' | 'my_listings' | 'my_deals';
type DealStatus =
  | 'proposed' | 'accepted' | 'paid' | 'published'
  | 'confirmed' | 'released' | 'disputed';

interface Listing {
  id: string;
  type: ListingType;
  channel: {
    id: string;
    name: string;
    avatar?: string;
    subscribers: number;
    er: number;
    topic: string;
    verified: boolean;
    rating: number;
  };
  price: number;
  currency: 'USD' | 'USDT';
  description: string;
  requirements?: string;
  createdAt: string;
}

interface DealMessage {
  id: string;
  from: string;
  text: string;
  ts: string;
}

interface Deal {
  id: string;
  listingId: string;
  channelName: string;
  status: DealStatus;
  price: number;
  currency: 'USD' | 'USDT';
  autoReleaseAt?: string;
  messages?: DealMessage[];
}

const TOPICS = ['Crypto', 'Tech', 'News', 'Gaming', 'Finance', 'Lifestyle', 'Education'];

const STATUS_COLORS: Record<DealStatus, string> = {
  proposed: 'bg-slate-100 text-slate-700',
  accepted: 'bg-blue-100 text-blue-700',
  paid: 'bg-amber-100 text-amber-700',
  published: 'bg-indigo-100 text-indigo-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  released: 'bg-green-100 text-green-700',
  disputed: 'bg-red-100 text-red-700',
};

function formatSubs(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function Stars({ rating }: { rating: number }): JSX.Element {
  return (
    <div className="flex items-center gap-0.5" aria-label={`rating-${rating}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={i <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}
        />
      ))}
      <span className="text-xs text-brand-muted ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

interface CreateForm {
  type: ListingType;
  channel: string;
  price: number;
  description: string;
  requirements: string;
}

interface DealsQueryData { items: Deal[] }
interface ListingsQueryData { items: Listing[] }

/** Returns true if the axios/fetch error is a 404 or 502 (service not ready). */
function isServiceUnavailable(e: unknown): boolean {
  if (e && typeof e === 'object') {
    const status = (e as { response?: { status?: number }; status?: number })?.response?.status
      ?? (e as { status?: number })?.status;
    if (status === 404 || status === 502) return true;
  }
  return false;
}

export function Exchange(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [tab, setTab] = useState<TabKey>('ads');
  const [search, setSearch] = useState('');
  const [topicFilter, setTopicFilter] = useState<string[]>([]);
  const [subsMin, setSubsMin] = useState(1000);
  const [subsMax, setSubsMax] = useState(1_000_000);
  const [erMin, setErMin] = useState(0);
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(10000);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const listingsQuery = useQuery<ListingsQueryData, unknown, ListingsQueryData & { serviceDown?: boolean }>({
    queryKey: ['exchange', 'listings', tab],
    queryFn: async () => {
      const typeParam = tab === 'vp' ? 'vp_partnership' : tab === 'ads' ? 'ad_placement' : tab;
      try {
        const { data } = await api.get('/exchange/listings', { params: { type: typeParam } });
        return data as ListingsQueryData;
      } catch (e) {
        if (isServiceUnavailable(e)) {
          return { items: [], serviceDown: true } as ListingsQueryData & { serviceDown: boolean };
        }
        throw e;
      }
    },
    enabled: tab === 'ads' || tab === 'vp' || tab === 'my_listings',
  });

  const dealsQuery = useQuery<DealsQueryData, unknown, DealsQueryData & { serviceDown?: boolean }>({
    queryKey: ['exchange', 'deals'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/exchange/deals');
        return data as DealsQueryData;
      } catch (e) {
        if (isServiceUnavailable(e)) {
          return { items: [], serviceDown: true } as DealsQueryData & { serviceDown: boolean };
        }
        throw e;
      }
    },
    enabled: tab === 'my_deals',
  });

  const createListing = useMutation({
    mutationFn: async (payload: CreateForm) => {
      const { data } = await api.post('/exchange/listings', payload);
      return data;
    },
    onSuccess: () => {
      toast.success('Объявление создано');
      setCreateOpen(false);
      void qc.invalidateQueries({ queryKey: ['exchange', 'listings'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createDeal = useMutation({
    mutationFn: async (listingId: string) => {
      const { data } = await api.post('/exchange/deals', { listingId });
      return data;
    },
    onSuccess: () => {
      toast.success('Сделка создана');
      void qc.invalidateQueries({ queryKey: ['exchange', 'deals'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dealAction = useMutation({
    mutationFn: async (vars: { id: string; action: 'accept' | 'confirm' | 'dispute' }) => {
      const { data } = await api.post(`/exchange/deals/${vars.id}/${vars.action}`, {});
      return data;
    },
    onSuccess: (_d, vars) => {
      toast.success(`Deal ${vars.action}`);
      void qc.invalidateQueries({ queryKey: ['exchange', 'deals'] });
      setSelectedDeal(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filteredListings = useMemo(() => {
    const items = listingsQuery.data?.items ?? [];
    return items.filter((l) => {
      if (search && !l.channel.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (topicFilter.length && !topicFilter.includes(l.channel.topic)) return false;
      if (l.channel.subscribers < subsMin || l.channel.subscribers > subsMax) return false;
      if (l.channel.er < erMin) return false;
      if (l.price < priceMin || l.price > priceMax) return false;
      if (verifiedOnly && !l.channel.verified) return false;
      return true;
    });
  }, [listingsQuery.data, search, topicFilter, subsMin, subsMax, erMin, priceMin, priceMax, verifiedOnly]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'ads', label: 'Реклама' },
    { key: 'vp', label: 'ВП (Взаимный пиар)' },
    { key: 'my_listings', label: 'Мои объявления' },
    { key: 'my_deals', label: 'Мои сделки' },
  ];

  return (
    <div className="p-6 space-y-4">
      {/* Topbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-brand-dark">{t('nav.exchange')}</h1>
        <button
          className="btn-primary flex items-center gap-2"
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={16} /> Создать объявление
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200" role="tablist">
        {tabs.map((tg) => (
          <button
            key={tg.key}
            role="tab"
            aria-selected={tab === tg.key}
            onClick={() => setTab(tg.key)}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium ${
              tab === tg.key
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-brand-muted hover:text-brand-dark'
            }`}
          >
            {tg.label}
          </button>
        ))}
      </div>

      {(tab === 'ads' || tab === 'vp') && (
        <FiltersBar
          search={search} setSearch={setSearch}
          topicFilter={topicFilter} setTopicFilter={setTopicFilter}
          subsMin={subsMin} setSubsMin={setSubsMin}
          subsMax={subsMax} setSubsMax={setSubsMax}
          erMin={erMin} setErMin={setErMin}
          priceMin={priceMin} setPriceMin={setPriceMin}
          priceMax={priceMax} setPriceMax={setPriceMax}
          verifiedOnly={verifiedOnly} setVerifiedOnly={setVerifiedOnly}
        />
      )}

      {/* Main content */}
      {tab === 'my_deals' ? (
        <DealsList
          isLoading={dealsQuery.isLoading}
          isError={dealsQuery.isError}
          serviceDown={!!(dealsQuery.data as (DealsQueryData & { serviceDown?: boolean }) | undefined)?.serviceDown}
          deals={dealsQuery.data?.items ?? []}
          onSelect={setSelectedDeal}
        />
      ) : (
        <ListingsGrid
          isLoading={listingsQuery.isLoading}
          isError={listingsQuery.isError}
          serviceDown={!!(listingsQuery.data as (ListingsQueryData & { serviceDown?: boolean }) | undefined)?.serviceDown}
          listings={filteredListings}
          tab={tab}
          onOrder={(id) => createDeal.mutate(id)}
        />
      )}

      {createOpen && (
        <CreateListingModal
          onClose={() => setCreateOpen(false)}
          onSubmit={(form) => createListing.mutate(form)}
          submitting={createListing.isPending}
        />
      )}

      {selectedDeal && (
        <DealSlideOut
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onConfirm={() => dealAction.mutate({ id: selectedDeal.id, action: 'confirm' })}
          onDispute={() => dealAction.mutate({ id: selectedDeal.id, action: 'dispute' })}
          onAccept={() => dealAction.mutate({ id: selectedDeal.id, action: 'accept' })}
          pending={dealAction.isPending}
        />
      )}
    </div>
  );
}

interface FiltersBarProps {
  search: string; setSearch: (s: string) => void;
  topicFilter: string[]; setTopicFilter: (v: string[]) => void;
  subsMin: number; setSubsMin: (n: number) => void;
  subsMax: number; setSubsMax: (n: number) => void;
  erMin: number; setErMin: (n: number) => void;
  priceMin: number; setPriceMin: (n: number) => void;
  priceMax: number; setPriceMax: (n: number) => void;
  verifiedOnly: boolean; setVerifiedOnly: (b: boolean) => void;
}

function FiltersBar(p: FiltersBarProps): JSX.Element {
  const toggleTopic = (tp: string): void => {
    p.setTopicFilter(p.topicFilter.includes(tp)
      ? p.topicFilter.filter((x) => x !== tp)
      : [...p.topicFilter, tp]);
  };

  return (
    <div className="card space-y-4">
      <div className="flex items-center gap-2">
        <Search size={16} className="text-brand-muted" />
        <input
          aria-label="search"
          type="text"
          className="input flex-1"
          placeholder="Поиск по каналу…"
          value={p.search}
          onChange={(e) => p.setSearch(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="text-sm font-medium self-center">Тематика:</span>
        {TOPICS.map((tp) => (
          <button
            key={tp}
            onClick={() => toggleTopic(tp)}
            className={`px-3 py-1 rounded-full text-xs border ${
              p.topicFilter.includes(tp)
                ? 'bg-brand-primary text-white border-brand-primary'
                : 'bg-white text-brand-muted border-slate-200'
            }`}
          >
            {tp}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-medium mb-1">
            Подписчики: {formatSubs(p.subsMin)} – {formatSubs(p.subsMax)}
          </label>
          <input
            aria-label="subs-min" type="range" min={1000} max={1_000_000} step={1000}
            value={p.subsMin} onChange={(e) => p.setSubsMin(Number(e.target.value))}
            className="w-full"
          />
          <input
            aria-label="subs-max" type="range" min={1000} max={1_000_000} step={1000}
            value={p.subsMax} onChange={(e) => p.setSubsMax(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">ER min: {p.erMin.toFixed(1)}%</label>
          <input
            aria-label="er-min" type="range" min={0} max={30} step={0.5}
            value={p.erMin} onChange={(e) => p.setErMin(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">
            Цена: ${p.priceMin} – ${p.priceMax}
          </label>
          <input
            aria-label="price-min" type="range" min={0} max={10000} step={50}
            value={p.priceMin} onChange={(e) => p.setPriceMin(Number(e.target.value))}
            className="w-full"
          />
          <input
            aria-label="price-max" type="range" min={0} max={10000} step={50}
            value={p.priceMax} onChange={(e) => p.setPriceMax(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              aria-label="verified-only" type="checkbox"
              checked={p.verifiedOnly}
              onChange={(e) => p.setVerifiedOnly(e.target.checked)}
            />
            <ShieldCheck size={16} className="text-emerald-600" />
            <span className="text-sm">Только верифицированные</span>
          </label>
        </div>
      </div>
    </div>
  );
}

interface ListingsGridProps {
  isLoading: boolean;
  isError: boolean;
  serviceDown: boolean;
  listings: Listing[];
  tab: TabKey;
  onOrder: (listingId: string) => void;
}

function ListingsGrid({ isLoading, isError, serviceDown, listings, tab, onOrder }: ListingsGridProps): JSX.Element {
  if (isLoading) return <div className="card text-brand-muted">Загрузка…</div>;
  if (isError) return <div className="card text-red-600">Ошибка загрузки</div>;
  if (serviceDown || listings.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-brand-muted">
          {serviceDown ? 'Marketplace is warming up…' : 'Нет объявлений по выбранным фильтрам'}
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="listings-grid">
      {listings.map((l) => (
        <div key={l.id} className="card flex flex-col gap-3" data-testid="listing-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary font-bold">
              {l.channel.name.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="font-semibold truncate">{l.channel.name}</span>
                {l.channel.verified && <ShieldCheck size={14} className="text-emerald-600" />}
              </div>
              <Stars rating={l.channel.rating} />
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-brand-muted">
            <span>{formatSubs(l.channel.subscribers)} подп.</span>
            <span>·</span>
            <span>ER {l.channel.er.toFixed(1)}%</span>
          </div>
          <span className="inline-block px-2 py-1 bg-slate-100 rounded text-xs w-fit">
            {l.channel.topic}
          </span>
          <p className="text-sm text-slate-700 line-clamp-2">{l.description}</p>
          <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-100">
            <span className="text-lg font-bold text-brand-primary">
              {l.price} {l.currency}
            </span>
            <button
              className="btn-primary text-sm"
              onClick={() => onOrder(l.id)}
              data-testid={`order-${l.id}`}
            >
              {tab === 'vp' || l.type === 'vp_partnership' ? 'ВП-запрос' : 'Заказать'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

interface DealsListProps {
  isLoading: boolean;
  isError: boolean;
  serviceDown: boolean;
  deals: Deal[];
  onSelect: (d: Deal) => void;
}

function DealsList({ isLoading, isError, serviceDown, deals, onSelect }: DealsListProps): JSX.Element {
  if (isLoading) return <div className="card text-brand-muted">Загрузка…</div>;
  if (isError) return <div className="card text-red-600">Ошибка загрузки</div>;
  if (serviceDown || deals.length === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-brand-muted">No active deals</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="deals-list">
      {deals.map((d) => (
        <button
          key={d.id}
          onClick={() => onSelect(d)}
          className="card w-full text-left hover:shadow-md transition"
          data-testid={`deal-${d.id}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-semibold">{d.channelName}</div>
              <div className="text-sm text-brand-muted">
                {d.price} {d.currency} · #{d.id.slice(0, 8)}
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[d.status]}`}>
              {d.status}
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

interface CreateListingModalProps {
  onClose: () => void;
  onSubmit: (form: CreateForm) => void;
  submitting: boolean;
}

function CreateListingModal({ onClose, onSubmit, submitting }: CreateListingModalProps): JSX.Element {
  const [form, setForm] = useState<CreateForm>({
    type: 'ad_placement',
    channel: '',
    price: 100,
    description: '',
    requirements: '',
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog" aria-label="create-listing"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Создать объявление</h2>
          <button onClick={onClose} aria-label="close-modal">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Тип</label>
            <select
              aria-label="listing-type"
              className="input w-full"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as ListingType })}
            >
              <option value="ad_placement">Реклама (платно)</option>
              <option value="vp_partnership">ВП (взаимный пиар)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Канал</label>
            <input
              aria-label="channel" required
              className="input w-full"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              placeholder="@my_channel"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Цена ($)</label>
            <input
              aria-label="price" type="number" min={0} required
              className="input w-full"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Описание</label>
            <textarea
              aria-label="description" required
              className="input w-full min-h-[80px]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Требования</label>
            <textarea
              aria-label="requirements"
              className="input w-full min-h-[60px]"
              value={form.requirements}
              onChange={(e) => setForm({ ...form, requirements: e.target.value })}
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Создаём…' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DealSlideOutProps {
  deal: Deal;
  onClose: () => void;
  onConfirm: () => void;
  onDispute: () => void;
  onAccept: () => void;
  pending: boolean;
}

function DealSlideOut({ deal, onClose, onConfirm, onDispute, onAccept, pending }: DealSlideOutProps): JSX.Element {
  const [chat, setChat] = useState('');
  const stages: DealStatus[] = ['proposed', 'accepted', 'paid', 'published', 'confirmed', 'released'];
  const currentIdx = stages.indexOf(deal.status);

  const autoReleaseLeft = useMemo(() => {
    if (!deal.autoReleaseAt) return null;
    const ms = new Date(deal.autoReleaseAt).getTime() - Date.now();
    if (ms <= 0) return '0:00:00';
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  }, [deal.autoReleaseAt]);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-label="deal-details">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="font-semibold">{deal.channelName}</div>
            <div className="text-xs text-brand-muted">#{deal.id.slice(0, 8)}</div>
          </div>
          <button onClick={onClose} aria-label="close-deal"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4 flex-1 overflow-y-auto">
          {/* Escrow status */}
          <div>
            <div className="text-sm font-medium mb-2">Эскроу статус</div>
            <div className="flex items-center gap-1">
              {stages.map((s, i) => (
                <div key={s} className="flex-1 flex flex-col items-center">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    i <= currentIdx ? 'bg-brand-primary text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {i + 1}
                  </div>
                  <div className="text-[10px] mt-1 text-center">{s}</div>
                </div>
              ))}
            </div>
            {deal.status === 'disputed' && (
              <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                <AlertTriangle size={14} /> Спор открыт
              </div>
            )}
          </div>

          {/* Auto-release */}
          {autoReleaseLeft && (
            <div className="card bg-amber-50 border-amber-200">
              <div className="flex items-center gap-2 text-sm">
                <Clock size={14} className="text-amber-600" />
                <span>Авто-релиз через: <strong>{autoReleaseLeft}</strong></span>
              </div>
            </div>
          )}

          {/* Chat */}
          <div>
            <div className="text-sm font-medium mb-2 flex items-center gap-1">
              <MessageSquare size={14} /> Чат
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto bg-slate-50 rounded p-2" data-testid="deal-chat">
              {(deal.messages ?? []).length === 0 ? (
                <div className="text-xs text-brand-muted">Сообщений пока нет</div>
              ) : (
                deal.messages?.map((m) => (
                  <div key={m.id} className="text-sm">
                    <span className="font-medium">{m.from}:</span> {m.text}
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <input
                aria-label="chat-input"
                className="input flex-1"
                value={chat}
                onChange={(e) => setChat(e.target.value)}
                placeholder="Сообщение…"
              />
              <button className="btn-secondary" onClick={() => setChat('')} aria-label="send-message">
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex gap-2 flex-wrap">
          {deal.status === 'proposed' && (
            <button className="btn-primary flex-1" disabled={pending} onClick={onAccept}>
              Принять
            </button>
          )}
          {(deal.status === 'published' || deal.status === 'paid') && (
            <button
              className="btn-primary flex-1 flex items-center justify-center gap-1"
              disabled={pending}
              onClick={onConfirm}
            >
              <CheckCircle2 size={14} /> Подтвердить
            </button>
          )}
          {deal.status !== 'released' && deal.status !== 'disputed' && (
            <button
              className="btn-secondary flex items-center justify-center gap-1"
              disabled={pending}
              onClick={onDispute}
            >
              <AlertTriangle size={14} /> Спор
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
